// verificationLedger.js — E2 (super-marathon) last-verification store for the Home card.
//
// The Chat seal is driven by per-message metadata (attached live in SessionWorkspace).
// The Home card ("última entrega ✓ verificada") needs the SAME data to survive a reload,
// so the most recent verification_report is mirrored to localStorage here. This module
// INTENTIONALLY touches window.localStorage (like sprintFlags.js) and is excluded from
// the lib-purity test. It is only ever written when apolo_verification_report is ON — a
// fresh install with the flag OFF never records, so the Home card reads null and hides.

const KEY = 'apolo_last_verification';

/**
 * @typedef {{ reachedGreen: boolean, noVerifiers: boolean,
 *   verifiers: { name: string, passed: boolean }[], at?: number }} VerificationRecord
 */

/**
 * Persist the most recent verification report (best-effort; localStorage may be absent).
 * @param {{ reachedGreen?: boolean, noVerifiers?: boolean, verifiers?: { name: string, passed: boolean }[] }} report
 * @param {number} [at] epoch ms (injectable for tests; defaults to Date.now())
 */
export function recordVerification(report, at = Date.now()) {
  if (typeof window === 'undefined' || !report) return;
  try {
    /** @type {VerificationRecord} */
    const rec = {
      reachedGreen: !!report.reachedGreen,
      noVerifiers: !!report.noVerifiers,
      verifiers: Array.isArray(report.verifiers)
        ? report.verifiers.map((v) => ({ name: String(v?.name ?? ''), passed: !!v?.passed }))
        : [],
      at,
    };
    window.localStorage.setItem(KEY, JSON.stringify(rec));
  } catch {
    // private mode / quota / serialization → skip (the card just stays empty)
  }
}

/**
 * Read the most recent verification report, or null if none/unreadable.
 * @returns {VerificationRecord | null}
 */
export function readLastVerification() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      reachedGreen: !!parsed.reachedGreen,
      noVerifiers: !!parsed.noVerifiers,
      verifiers: Array.isArray(parsed.verifiers)
        ? parsed.verifiers.map((v) => ({ name: String(v?.name ?? ''), passed: !!v?.passed }))
        : [],
      at: typeof parsed.at === 'number' ? parsed.at : undefined,
    };
  } catch {
    return null;
  }
}
